import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { Star, ShieldCheck, Clock, MapPin, Filter, CheckCircle2, ArrowRight, Navigation, Truck, Camera } from 'lucide-react';
import { detectServiceFromPrompt, getServiceConfig, setActiveService, ServiceId, SupplierCard } from '../lib/serviceData';
import { BiddingPanel } from '../components/BiddingPanel';
import { RequestBetterOfferModal } from '../components/RequestBetterOfferModal';
import { openLogin } from '../lib/authStore';
import { useAuth } from '../lib/authStore';
import { ConversationTurn } from '../lib/agenticAgent';
import { loadConversation, saveConversation } from '../lib/coPilotStore';
import { parseCoPilotIntent, generateCoPilotReply, CoPilotContext } from '../lib/coPilotAgent';
import { createOffer } from '../lib/activityStore';
import { Sparkles, Send, Loader2 } from 'lucide-react';
const SORT_OPTIONS = ['Recommended', 'Fastest ETA', 'Lowest Price', 'Highest Rated'] as const;
type SortOption = (typeof SORT_OPTIONS)[number];
function ToggleRow({
  label,
  value,
  onChange
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return <button onClick={() => onChange(!value)} className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-5392">
      <span className="text-sm text-gray-700" data-id="element-5393">{label}</span>
      <span className={`relative w-9 h-5 rounded-full transition-colors ${value ? 'bg-brand-500' : 'bg-gray-200'}`} data-id="element-5394">
        <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${value ? 'translate-x-4' : 'translate-x-0.5'}`} data-id="element-5395" />
      </span>
    </button>;
}
// Parse ETA strings like "15 mins", "1 hr", "2.5 hrs", "Next-day start" into a sortable minutes number.
function etaToMinutes(eta: string): number {
  const lower = eta.toLowerCase();
  if (lower.includes('next-day') || lower.includes('day')) return 24 * 60;
  const m = lower.match(/(\d+(?:\.\d+)?)\s*(min|hr|hour)/);
  if (!m) return 9999;
  const n = parseFloat(m[1]);
  return m[2].startsWith('hr') || m[2].startsWith('hour') ? n * 60 : n;
}
export function SupplierResults() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isSignedIn
  } = useAuth();
  const [selectedSupplier, setSelectedSupplier] = useState<number | null>(null);
  // Co-pilot state
  const [aiMode, setAiMode] = useState(false);
  const [turns, setTurns] = useState<ConversationTurn[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isAiTyping, setIsAiTyping] = useState(false);
  const [priceMultipliers, setPriceMultipliers] = useState<Record<string, number>>({});
  const [dynamicPickup, setDynamicPickup] = useState<string | null>(null);
  const [dynamicDropoff, setDynamicDropoff] = useState<string | null>(null);
  const [highlightedSupplier, setHighlightedSupplier] = useState<string | null>(null);
  // Load conversation on mount
  useEffect(() => {
    setTurns(loadConversation());
  }, []);
  // Filter / sort state
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedSort, setSelectedSort] = useState<SortOption>('Recommended');
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [insuredOnly, setInsuredOnly] = useState(false);
  const [maxPrice, setMaxPrice] = useState(0); // 0 means "Any"
  const [offerTarget, setOfferTarget] = useState<{
    name: string;
    image?: string;
    truck?: string;
    price: number;
  } | null>(null);
  const stateService = location.state?.service as ServiceId | undefined;
  const statePrompt = location.state?.prompt as string | undefined;
  const statePhotos = location.state?.photos as string[] | undefined;
  // Photos can come from navigation state or sessionStorage (refresh resilience).
  const photos: string[] = useMemo(() => {
    if (statePhotos && statePhotos.length) return statePhotos;
    if (typeof window === 'undefined') return [];
    try {
      const raw = sessionStorage.getItem('pair2.lastPhotos');
      return raw ? JSON.parse(raw) as string[] : [];
    } catch {
      return [];
    }
  }, [statePhotos]);
  const resolvedServiceId: ServiceId = stateService || (statePrompt ? detectServiceFromPrompt(statePrompt) : undefined) || (typeof window !== 'undefined' ? sessionStorage.getItem('pair2.activeService') as ServiceId || 'home' : 'home');
  const service = useMemo(() => getServiceConfig(resolvedServiceId), [resolvedServiceId]);
  // Keep storage in sync so the AI widget reflects the same service
  useEffect(() => {
    setActiveService(service.id, statePrompt);
  }, [service.id, statePrompt]);
  // Apply filters + sort
  // Apply dynamic pricing from AI co-pilot
  const dynamicSuppliers = useMemo(() => {
    return service.suppliers.map(s => ({
      ...s,
      price: priceMultipliers[s.name] ? Math.round(s.price * priceMultipliers[s.name]) : s.price
    }));
  }, [service.suppliers, priceMultipliers]);
  const visibleSuppliers = useMemo(() => {
    let list = [...dynamicSuppliers];
    if (verifiedOnly) list = list.filter(s => s.verified);
    if (insuredOnly) list = list.filter(s => s.features.some(f => /insur/i.test(f)));
    if (maxPrice > 0) list = list.filter(s => s.price <= maxPrice);
    switch (selectedSort) {
      case 'Fastest ETA':
        list.sort((a, b) => etaToMinutes(a.eta) - etaToMinutes(b.eta));
        break;
      case 'Lowest Price':
        list.sort((a, b) => a.price - b.price);
        break;
      case 'Highest Rated':
        list.sort((a, b) => b.rating - a.rating);
        break;
      default:
        // Recommended = keep original order (badge-first authors curated it)
        break;
    }
    return list;
  }, [dynamicSuppliers, selectedSort, verifiedOnly, insuredOnly, maxPrice]);
  const activeFilterCount = (selectedSort !== 'Recommended' ? 1 : 0) + (verifiedOnly ? 1 : 0) + (insuredOnly ? 1 : 0) + (maxPrice > 0 ? 1 : 0);
  const filteredCount = visibleSuppliers.length;
  // Co-pilot chat handler
  const handleCoPilotSubmit = () => {
    if (!chatInput.trim()) return;
    const userText = chatInput.trim();
    setChatInput('');
    const newTurn: ConversationTurn = {
      id: Date.now(),
      role: 'user',
      text: userText
    };
    setTurns(prev => [...prev, newTurn]);
    setIsAiTyping(true);
    setTimeout(() => {
      const context: CoPilotContext = {
        suppliers: dynamicSuppliers,
        pickup: dynamicPickup || service.summary.pickup || '',
        dropoff: dynamicDropoff || service.summary.dropoff || '',
        verifiedOnly,
        insuredOnly,
        currentSort: selectedSort
      };
      const intent = parseCoPilotIntent(userText, context);
      const replyText = generateCoPilotReply(intent, context);
      // Apply side effects
      if (intent.type === 'discount') {
        const supplier = dynamicSuppliers.find(s => s.name === intent.supplierName);
        if (supplier) {
          const newPrice = Math.round(supplier.price * (1 - intent.percentage / 100));
          createOffer({
            jobId: 'demo',
            supplierName: supplier.name,
            serviceName: service.name,
            originalPrice: supplier.price,
            currentPrice: newPrice,
            status: 'pending',
            currentSide: 'supplier',
            history: [{
              by: 'user',
              price: newPrice,
              message: `AI requested ${intent.percentage}% discount.`,
              at: Date.now()
            }]
          });
        }
      } else if (intent.type === 'change-dest') {
        if (intent.field === 'pickup') setDynamicPickup(intent.newLocation);else setDynamicDropoff(intent.newLocation);
        // Randomize prices slightly to simulate recalculation
        const newMultipliers = {
          ...priceMultipliers
        };
        dynamicSuppliers.forEach(s => {
          newMultipliers[s.name] = 0.85 + Math.random() * 0.4; // 0.85x to 1.25x
        });
        setPriceMultipliers(newMultipliers);
      } else if (intent.type === 'filter') {
        if (intent.filterType === 'verified') setVerifiedOnly(intent.enable);
        if (intent.filterType === 'insured') setInsuredOnly(intent.enable);
      } else if (intent.type === 'sort') {
        if (intent.sortBy === 'price') setSelectedSort('Lowest Price');
        if (intent.sortBy === 'eta') setSelectedSort('Fastest ETA');
        if (intent.sortBy === 'rating') setSelectedSort('Highest Rated');
      } else if (intent.type === 'explain' && intent.supplierName) {
        setHighlightedSupplier(intent.supplierName);
        setTimeout(() => setHighlightedSupplier(null), 3000);
      }
      const aiTurn: ConversationTurn = {
        id: Date.now() + 1,
        role: 'ai',
        text: replyText
      };
      setTurns(prev => {
        const next = [...prev, aiTurn];
        saveConversation(next);
        return next;
      });
      setIsAiTyping(false);
    }, 1200);
  };
  return <div className="h-[calc(100vh-5rem)] flex flex-col lg:flex-row overflow-hidden bg-surface-100" data-id="element-5396">
      {/* Left Side - Map Area */}
      <div className={`hidden lg:block relative bg-gray-200 transition-all duration-500 ease-in-out ${aiMode ? 'w-1/4' : 'w-1/2'}`} data-id="element-5397">
        <ResultsMap service={service} dynamicPickup={dynamicPickup} dynamicDropoff={dynamicDropoff} data-id="element-5398" />
      </div>

      {/* Middle - AI Co-pilot Chat (Only visible in AI Mode) */}
      {aiMode && <div className="w-full lg:w-1/3 flex flex-col h-full bg-white/70 backdrop-blur-xl border-l border-white/40 shadow-[0_0_40px_rgba(0,0,0,0.05)] z-20 relative" data-id="element-5399">
          <div className="absolute inset-0 bg-gradient-to-b from-brand-50/30 to-transparent pointer-events-none" data-id="element-5400" />

          <div className="px-5 py-4 border-b border-gray-100/50 flex items-center gap-3 bg-white/50 backdrop-blur-md sticky top-0 z-10" data-id="element-5401">
            <div className="w-8 h-8 rounded-full orange-gradient-bg flex items-center justify-center shadow-sm" data-id="element-5402">
              <Sparkles className="w-4 h-4 text-white" data-id="element-5403" />
            </div>
            <div data-id="element-5404">
              <h3 className="font-bold text-gray-900 text-sm" data-id="element-5405">
                Pair2 AI Co-pilot
              </h3>
              <p className="text-[10px] text-gray-500" data-id="element-5406">
                Negotiate, filter, and modify
              </p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-4 relative" data-id="element-5407">
            {turns.map(turn => <div key={turn.id} className={`flex ${turn.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-5408">
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${turn.role === 'user' ? 'bg-gray-900 text-white rounded-tr-sm' : 'bg-white border border-gray-100 text-gray-800 rounded-tl-sm'}`} data-id="element-5409">
                  {turn.role === 'ai' && <div className="flex items-center gap-2 mb-1" data-id="element-5410">
                      <Sparkles className="w-3 h-3 text-brand-500" data-id="element-5411" />
                      <span className="text-[10px] font-bold uppercase tracking-wider text-brand-500" data-id="element-5412">
                        Pair2 AI
                      </span>
                    </div>}
                  <div className="leading-relaxed whitespace-pre-wrap" data-id="element-5413">
                    {turn.text}
                  </div>
                </div>
              </div>)}
            {isAiTyping && <div className="flex justify-start" data-id="element-5414">
                <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm flex items-center gap-1.5" data-id="element-5415">
                  <div className="w-1.5 h-1.5 rounded-full bg-brand-400 animate-bounce" style={{
              animationDelay: '0ms'
            }} data-id="element-5416" />
                  <div className="w-1.5 h-1.5 rounded-full bg-brand-400 animate-bounce" style={{
              animationDelay: '150ms'
            }} data-id="element-5417" />
                  <div className="w-1.5 h-1.5 rounded-full bg-brand-400 animate-bounce" style={{
              animationDelay: '300ms'
            }} data-id="element-5418" />
                </div>
              </div>}
          </div>

          <div className="p-4 bg-white/80 backdrop-blur-md border-t border-gray-100/50" data-id="element-5419">
            <div className="flex items-center gap-2 bg-white rounded-2xl px-3 py-2 border border-gray-200 focus-within:border-brand-300 focus-within:ring-2 focus-within:ring-brand-50 transition-all shadow-sm" data-id="element-5420">
              <input type="text" value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleCoPilotSubmit()} placeholder="Ask for a discount, change dropoff..." className="flex-1 bg-transparent outline-none text-sm placeholder:text-gray-400 py-1" data-id="element-5421" />
              <button onClick={handleCoPilotSubmit} disabled={!chatInput.trim() || isAiTyping} className="w-8 h-8 rounded-full orange-gradient-bg text-white flex items-center justify-center disabled:opacity-40 transition-opacity shrink-0" data-id="element-5422">
                <Send className="w-3.5 h-3.5" data-id="element-5423" />
              </button>
            </div>
          </div>
        </div>}

      {/* Right Side - Results List */}
      <div className={`w-full flex flex-col h-full bg-surface-100 border-l border-gray-200 transition-all duration-500 ease-in-out ${aiMode ? 'lg:w-[41.666%]' : 'lg:w-1/2'}`} data-id="element-5424">
        <div className="p-6 bg-white border-b border-gray-100 shrink-0 z-10 shadow-sm relative" data-id="element-5425">
          <div className="flex justify-between items-start gap-4" data-id="element-5426">
            <div className="min-w-0 flex-1" data-id="element-5427">
              <div className="flex items-center gap-2 mb-2 flex-wrap" data-id="element-5428">
                <span className="inline-flex items-center gap-1.5 bg-brand-50 text-brand-700 px-2.5 py-1 rounded-full text-[11px] font-bold" data-id="element-5429">
                  {service.name}
                </span>
                {(statePrompt || photos.length > 0) && <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium text-gray-600 bg-gray-50 border border-gray-100 max-w-full" data-id="element-5430">
                    <span className="text-gray-400" data-id="element-5431">Your request:</span>
                    <span className="font-semibold text-gray-800 truncate max-w-[16ch] sm:max-w-[26ch]" data-id="element-5432">
                      "{statePrompt || service.defaultPrompt}"
                    </span>
                    {photos.length > 0 && <span className="flex items-center gap-1 pl-1.5 border-l border-gray-200 text-gray-500" data-id="element-5433">
                        <Camera className="w-3 h-3" data-id="element-5434" />
                        {photos.length}
                      </span>}
                  </span>}
              </div>
              <ResultsHeader visibleSuppliers={visibleSuppliers} trucksNearby={service.trucksNearby} selectedSort={selectedSort} activeFilterCount={activeFilterCount} data-id="element-5435" />
            </div>
            <div className="relative shrink-0 flex flex-col items-end gap-3" data-id="element-5436">
              {/* AI Mode Toggle */}
              <div className="flex items-center bg-gray-100 p-1 rounded-xl" data-id="element-5437">
                <button onClick={() => setAiMode(false)} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${!aiMode ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-5438">
                  Normal
                </button>
                <button onClick={() => setAiMode(true)} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${aiMode ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-5439">
                  <Sparkles className={`w-3 h-3 ${aiMode ? 'text-brand-400' : ''}`} data-id="element-5440" />
                  AI Mode
                </button>
              </div>

              <button onClick={() => setFiltersOpen(v => !v)} className={`px-3 py-2 border rounded-xl flex items-center gap-2 text-sm font-medium transition-colors ${filtersOpen || activeFilterCount > 0 ? 'border-brand-300 bg-brand-50 text-brand-700' : 'border-gray-200 hover:bg-gray-50 text-gray-700'}`} data-id="element-5441">
                <Filter className="w-4 h-4" data-id="element-5442" /> Filters
                {activeFilterCount > 0 && <span className="bg-brand-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1.5 flex items-center justify-center" data-id="element-5443">
                    {activeFilterCount}
                  </span>}
              </button>

              {/* Filters popover */}
              {filtersOpen && <>
                  <div className="fixed inset-0 z-30" onClick={() => setFiltersOpen(false)} data-id="element-5444" />
                  <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-2xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.18)] border border-gray-100 z-40 overflow-hidden" data-id="element-5445">
                    <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between" data-id="element-5446">
                      <h4 className="font-bold text-gray-900 text-sm" data-id="element-5447">
                        Sort & filter
                      </h4>
                      <button onClick={() => {
                    setSelectedSort('Recommended');
                    setVerifiedOnly(false);
                    setInsuredOnly(false);
                    setMaxPrice(0);
                  }} className="text-xs font-medium text-gray-500 hover:text-brand-600" data-id="element-5448">
                        Reset
                      </button>
                    </div>

                    <div className="p-5 space-y-5" data-id="element-5449">
                      {/* Sort */}
                      <div data-id="element-5450">
                        <p className="text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-2" data-id="element-5451">
                          Sort by
                        </p>
                        <div className="space-y-1" data-id="element-5452">
                          {SORT_OPTIONS.map(opt => <label key={opt} className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-colors ${selectedSort === opt ? 'bg-brand-50' : 'hover:bg-gray-50'}`} data-id="element-5453">
                              <span className={`text-sm ${selectedSort === opt ? 'font-semibold text-brand-700' : 'text-gray-700'}`} data-id="element-5454">
                                {opt}
                              </span>
                              <input type="radio" name="sort" checked={selectedSort === opt} onChange={() => setSelectedSort(opt)} className="accent-brand-500" data-id="element-5455" />
                            </label>)}
                        </div>
                      </div>

                      {/* Toggles */}
                      <div data-id="element-5456">
                        <p className="text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-2" data-id="element-5457">
                          Carrier requirements
                        </p>
                        <div className="space-y-2" data-id="element-5458">
                          <ToggleRow label="Verified carriers only" value={verifiedOnly} onChange={setVerifiedOnly} data-id="element-5459" />
                          <ToggleRow label="Insurance included" value={insuredOnly} onChange={setInsuredOnly} data-id="element-5460" />
                        </div>
                      </div>

                      {/* Max price */}
                      <div data-id="element-5461">
                        <div className="flex items-center justify-between mb-2" data-id="element-5462">
                          <p className="text-[11px] uppercase tracking-wider font-bold text-gray-500" data-id="element-5463">
                            Max price
                          </p>
                          <p className="text-xs font-semibold text-gray-900" data-id="element-5464">
                            {maxPrice > 0 ? `AED ${maxPrice}` : 'Any'}
                          </p>
                        </div>
                        <input type="range" min={0} max={3000} step={50} value={maxPrice} onChange={e => setMaxPrice(parseInt(e.target.value))} className="w-full accent-brand-500" data-id="element-5465" />
                      </div>
                    </div>

                    <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex justify-end" data-id="element-5466">
                      <button onClick={() => setFiltersOpen(false)} className="px-4 py-2 bg-gray-900 hover:bg-black text-white text-xs font-semibold rounded-lg transition-colors" data-id="element-5467">
                        Show {filteredCount} carrier
                        {filteredCount === 1 ? '' : 's'}
                      </button>
                    </div>
                  </div>
                </>}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4" data-id="element-5468">
          <BiddingPanel serviceId={service.id} serviceName={service.name} prompt={statePrompt} routeHeadline={service.summary.routeHeadline} pickup={service.summary.pickup} dropoff={service.summary.dropoff} estimatedPrice={service.summary.total} photos={photos} data-id="element-5469" />
          <div className="flex items-center gap-3 pt-1" data-id="element-5470">
            <div className="h-px bg-gray-200 flex-1" data-id="element-5471" />
            <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400" data-id="element-5472">
              Instant Book
            </span>
            <div className="h-px bg-gray-200 flex-1" data-id="element-5473" />
          </div>
          {visibleSuppliers.length === 0 && <div className="bg-white rounded-3xl border border-dashed border-gray-200 p-8 text-center" data-id="element-5474">
              <p className="font-semibold text-gray-700 mb-1" data-id="element-5475">
                No carriers match these filters
              </p>
              <p className="text-sm text-gray-500 mb-4" data-id="element-5476">
                Try relaxing your requirements or open the request for bidding
                above.
              </p>
              <button onClick={() => {
            setVerifiedOnly(false);
            setInsuredOnly(false);
            setMaxPrice(0);
            setSelectedSort('Recommended');
          }} className="text-sm font-semibold text-brand-600 hover:text-brand-700" data-id="element-5477">
                Reset filters
              </button>
            </div>}
          {visibleSuppliers.map((supplier, i) => <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.08
        }} key={supplier.id} onClick={() => setSelectedSupplier(supplier.id)} className={`bg-white rounded-3xl p-5 border-2 transition-all cursor-pointer ${highlightedSupplier === supplier.name ? 'border-brand-400 shadow-[0_0_20px_rgba(251,146,60,0.3)] ring-4 ring-brand-50' : selectedSupplier === supplier.id ? 'border-brand-500 shadow-[0_8px_30px_rgba(20,184,166,0.15)]' : 'border-transparent shadow-sm hover:border-gray-200 hover:shadow-md'}`} data-id="element-5478">
              <div className="flex gap-5" data-id="element-5479">
                <div className="w-32 h-24 rounded-2xl overflow-hidden shrink-0 bg-gray-100 relative" data-id="element-5480">
                  <img src={supplier.image} alt={supplier.name} className="w-full h-full object-cover" data-id="element-5481" />
                  {supplier.badge && <div className="absolute top-2 left-2 bg-brand-500 text-white text-[10px] font-bold px-2 py-1 rounded-md uppercase tracking-wider" data-id="element-5482">
                      {supplier.badge}
                    </div>}
                </div>

                <div className="flex-1 min-w-0" data-id="element-5483">
                  <div className="flex justify-between items-start mb-1 gap-2" data-id="element-5484">
                    <div className="flex items-center gap-2 min-w-0" data-id="element-5485">
                      <h3 className="font-bold text-gray-900 text-lg truncate" data-id="element-5486">
                        {supplier.name}
                      </h3>
                      {supplier.verified && <ShieldCheck className="w-5 h-5 text-blue-500 shrink-0" data-id="element-5487" />}
                    </div>
                    <div className="text-right shrink-0" data-id="element-5488">
                      <div className="font-display font-bold text-xl text-gray-900" data-id="element-5489">
                        AED {supplier.price}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 text-sm text-gray-500 mb-2 flex-wrap" data-id="element-5490">
                    <div className="flex items-center gap-1 text-amber-500 font-medium" data-id="element-5491">
                      <Star className="w-4 h-4 fill-current" data-id="element-5492" />{' '}
                      {supplier.rating}
                      <span className="text-gray-400 font-normal" data-id="element-5493">
                        ({supplier.reviews})
                      </span>
                    </div>
                    <span className="w-1 h-1 rounded-full bg-gray-300" data-id="element-5494" />
                    <div className="flex items-center gap-1" data-id="element-5495">
                      <Clock className="w-4 h-4" data-id="element-5496" /> {supplier.eta} away
                    </div>
                    <span className="w-1 h-1 rounded-full bg-gray-300" data-id="element-5497" />
                    <span className="font-medium text-gray-700" data-id="element-5498">
                      {supplier.truck}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-3" data-id="element-5499">
                    {supplier.features.map(feat => <span key={feat} className="px-2.5 py-1 bg-gray-50 text-gray-600 rounded-lg text-xs font-medium border border-gray-100 flex items-center gap-1" data-id="element-5500">
                        <CheckCircle2 className="w-3 h-3 text-brand-500" data-id="element-5501" />{' '}
                        {feat}
                      </span>)}
                  </div>
                </div>
              </div>

              {selectedSupplier === supplier.id && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} className="mt-5 pt-5 border-t border-gray-100 flex gap-3" data-id="element-5502">
                  <button onClick={e => {
              e.stopPropagation();
              setOfferTarget({
                name: supplier.name,
                image: supplier.image,
                truck: supplier.truck,
                price: supplier.price
              });
            }} className="flex-1 py-3 bg-white border-2 border-gray-200 hover:border-gray-300 text-gray-700 rounded-xl font-bold transition-colors" data-id="element-5503">
                    Request Better Offer
                  </button>
                  <button onClick={e => {
              e.stopPropagation();
              const goToPayment = () => navigate('/payment', {
                state: {
                  service: service.id,
                  prompt: statePrompt,
                  photos,
                  supplier: {
                    name: supplier.name,
                    price: supplier.price,
                    truck: supplier.truck
                  }
                }
              });
              if (isSignedIn) {
                goToPayment();
              } else {
                openLogin();
              }
            }} className="flex-1 py-3 bg-brand-500 hover:bg-brand-600 text-white rounded-xl font-bold transition-colors shadow-md shadow-brand-500/20 flex items-center justify-center gap-2" data-id="element-5504">
                    Book Now <ArrowRight className="w-4 h-4" data-id="element-5505" />
                  </button>
                </motion.div>}
            </motion.div>)}
        </div>
      </div>

      {offerTarget && <RequestBetterOfferModal open onClose={() => setOfferTarget(null)} serviceId={service.id} serviceName={service.name} prompt={statePrompt} routeHeadline={service.summary.routeHeadline} photos={photos} supplier={offerTarget} data-id="element-5506" />}
    </div>;
}
// ---- Search-results header --------------------------------------------------
// Dynamic stats — count, price range, average ETA, etc. Replaces the old static
// marketing copy ("Movers available now / Full-service home relocation crews").
function ResultsHeader({
  visibleSuppliers,
  trucksNearby,
  selectedSort,
  activeFilterCount
}: {
  visibleSuppliers: SupplierCard[];
  trucksNearby: number;
  selectedSort: string;
  activeFilterCount: number;
}) {
  const count = visibleSuppliers.length;
  if (count === 0) {
    return <>
        <h2 className="text-2xl font-display font-bold text-gray-900" data-id="element-5507">
          No carriers match your request
        </h2>
        <p className="text-sm text-gray-500 mt-1" data-id="element-5508">
          Try widening your filters or open this request for bidding to source
          new offers.
        </p>
      </>;
  }
  const prices = visibleSuppliers.map(s => s.price);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const avg = Math.round(prices.reduce((a, b) => a + b, 0) / prices.length);
  const etaMins = visibleSuppliers.map(s => parseInt(s.eta.replace(/[^\d]/g, ''), 10)).filter(n => Number.isFinite(n));
  const avgEta = etaMins.length ? Math.round(etaMins.reduce((a, b) => a + b, 0) / etaMins.length) : null;
  const avgRating = (visibleSuppliers.reduce((a, s) => a + s.rating, 0) / count).toFixed(1);
  const verified = visibleSuppliers.filter(s => s.verified).length;
  return <>
      <h2 className="text-2xl font-display font-bold text-gray-900" data-id="element-5509">
        {count} {count === 1 ? 'carrier' : 'carriers'} match your request
      </h2>
      <p className="text-sm text-gray-500 mt-1" data-id="element-5510">
        {verified} verified · {trucksNearby} trucks nearby · best price{' '}
        <span className="font-semibold text-gray-700" data-id="element-5511">AED {min}</span>
      </p>
      <div className="mt-3 flex flex-wrap gap-1.5" data-id="element-5512">
        <StatPill label="Avg price" value={`AED ${avg}`} data-id="element-5513" />
        <StatPill label="Range" value={`AED ${min}–${max}`} data-id="element-5514" />
        {avgEta !== null && <StatPill label="Avg ETA" value={`${avgEta} min`} data-id="element-5515" />}
        <StatPill label="Avg rating" value={`${avgRating} ★`} data-id="element-5516" />
      </div>
      <div className="mt-2.5 flex items-center gap-2 text-[11px] text-gray-500 flex-wrap" data-id="element-5517">
        <span className="font-medium text-gray-700" data-id="element-5518">Sorted by:</span>
        <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-medium" data-id="element-5519">
          {selectedSort}
        </span>
        {activeFilterCount > 0 && <span className="px-2 py-0.5 rounded-full bg-brand-50 text-brand-700 font-semibold border border-brand-100" data-id="element-5520">
            {activeFilterCount} filter{activeFilterCount === 1 ? '' : 's'}{' '}
            applied
          </span>}
      </div>
    </>;
}
function StatPill({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-50 border border-gray-100" data-id="element-5521">
      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400" data-id="element-5522">
        {label}
      </span>
      <span className="text-xs font-bold text-gray-900" data-id="element-5523">{value}</span>
    </div>;
}
// ---- Left-side results map with pickup/dropoff pins -----------------------
function ResultsMap({
  service,
  dynamicPickup,
  dynamicDropoff
}: {
  service: ReturnType<typeof getServiceConfig>;
  dynamicPickup?: string | null;
  dynamicDropoff?: string | null;
}) {
  const isSingle = service.id === 'recovery';
  const etaText = isSingle ? '18–24 min' : service.summary.pickupTime?.includes('Tomorrow') ? '~45 min trip' : '~32 min trip';
  const distanceText = isSingle ? null : '~22 km';
  return <div className="w-full h-full relative bg-gray-200 overflow-hidden" data-id="element-5524">
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&q=80&w=1600')] bg-cover bg-center opacity-60 grayscale" data-id="element-5525" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-900/10" data-id="element-5526" />

      {/* Top chips */}
      <div className="absolute top-6 left-6 right-6 z-20 flex gap-3" data-id="element-5527">
        <div className="glass-panel px-4 py-3 rounded-2xl flex items-center gap-3 shadow-md" data-id="element-5528">
          <div className="w-2.5 h-2.5 rounded-full bg-brand-500 shadow-[0_0_10px_rgba(251,146,60,0.9)] animate-pulse" data-id="element-5529" />
          <span className="font-medium text-gray-900 text-sm" data-id="element-5530">
            {service.trucksNearby} {service.shortName.toLowerCase()} nearby
          </span>
        </div>
        <div className="ml-auto glass-panel px-4 py-3 rounded-2xl flex items-center gap-2 shadow-md" data-id="element-5531">
          <Clock className="w-4 h-4 text-brand-500" data-id="element-5532" />
          <div className="leading-tight" data-id="element-5533">
            <p className="text-[9px] font-bold uppercase tracking-wider text-gray-500" data-id="element-5534">
              {isSingle ? 'Driver ETA' : 'Trip time'}
            </p>
            <p className="text-sm font-display font-bold text-gray-900" data-id="element-5535">
              {etaText}
            </p>
          </div>
        </div>
      </div>

      {/* Route line — light, brand-themed animated dots that flow along the path */}
      {!isSingle && <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="xMidYMid slice" data-id="element-5536">
          {/* Soft ambient halo behind the dots */}
          <path d="M 115 285 Q 200 230, 290 115" stroke="#fb923c" strokeWidth="8" strokeOpacity="0.12" fill="none" strokeLinecap="round" data-id="element-5537" />

          {/* Animated brand-orange dotted line that flows from pickup to dropoff */}
          <motion.path d="M 115 285 Q 200 230, 290 115" stroke="#fb923c" strokeWidth="3" strokeLinecap="round" strokeDasharray="0.5 9" fill="none" initial={{
        strokeDashoffset: 0
      }} animate={{
        strokeDashoffset: -38
      }} transition={{
        duration: 1.6,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-5538" />
        </svg>}

      {/* Pickup pin */}
      <motion.div initial={{
      scale: 0,
      y: 10
    }} animate={{
      scale: 1,
      y: 0
    }} transition={{
      type: 'spring',
      delay: 0.1,
      damping: 14
    }} className="absolute z-10" style={{
      left: isSingle ? '50%' : '28.75%',
      top: isSingle ? '50%' : '71.25%',
      transform: 'translate(-50%, -100%)'
    }} data-id="element-5539">
        <div className="relative flex flex-col items-center" data-id="element-5540">
          <div className="absolute -inset-1 rounded-full bg-brand-500/20 animate-ping" data-id="element-5541" />
          <div className="absolute -inset-2 rounded-full bg-brand-500/10" data-id="element-5542" />
          <div className="relative w-12 h-12 rounded-full orange-gradient-bg flex items-center justify-center shadow-xl border-[3px] border-white" data-id="element-5543">
            {isSingle ? <Navigation className="w-5 h-5 text-white" data-id="element-5544" /> : <Truck className="w-5 h-5 text-white" data-id="element-5545" />}
          </div>
          <div className="w-1.5 h-3 bg-gray-900/40 rounded-full mt-0.5" data-id="element-5546" />
        </div>
      </motion.div>

      {/* Dropoff pin */}
      {!isSingle && <motion.div initial={{
      scale: 0,
      y: 10
    }} animate={{
      scale: 1,
      y: 0
    }} transition={{
      type: 'spring',
      delay: 0.3,
      damping: 14
    }} className="absolute z-10" style={{
      left: '72.5%',
      top: '28.75%',
      transform: 'translate(-50%, -100%)'
    }} data-id="element-5547">
          <div className="relative flex flex-col items-center" data-id="element-5548">
            <div className="relative w-12 h-12 rounded-full bg-gray-900 flex items-center justify-center shadow-xl border-[3px] border-white" data-id="element-5549">
              <MapPin className="w-5 h-5 text-white" fill="currentColor" data-id="element-5550" />
            </div>
            <div className="w-1.5 h-3 bg-gray-900/40 rounded-full mt-0.5" data-id="element-5551" />
          </div>
        </motion.div>}

      {/* Bottom info card */}
      <div className="absolute bottom-6 left-6 right-6 z-20" data-id="element-5552">
        <div className="bg-white/95 backdrop-blur rounded-2xl shadow-xl border border-gray-100 p-4" data-id="element-5553">
          {isSingle ? <div className="flex items-center gap-3" data-id="element-5554">
              <div className="w-9 h-9 rounded-xl orange-gradient-bg flex items-center justify-center shrink-0" data-id="element-5555">
                <Navigation className="w-4 h-4 text-white" data-id="element-5556" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-5557">
                <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400" data-id="element-5558">
                  On-site location
                </p>
                <p className="font-bold text-gray-900 truncate" data-id="element-5559">
                  {dynamicPickup || service.summary.pickup}
                </p>
              </div>
            </div> : <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center" data-id="element-5560">
              <div className="flex items-center gap-2.5 min-w-0" data-id="element-5561">
                <div className="w-9 h-9 rounded-xl orange-gradient-bg flex items-center justify-center shrink-0" data-id="element-5562">
                  <Truck className="w-4 h-4 text-white" data-id="element-5563" />
                </div>
                <div className="min-w-0" data-id="element-5564">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400" data-id="element-5565">
                    Pickup
                  </p>
                  <p className="font-bold text-gray-900 text-sm truncate" data-id="element-5566">
                    {dynamicPickup || service.summary.pickup}
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-center justify-center text-gray-300" data-id="element-5567">
                <svg width="40" height="2" data-id="element-5568">
                  <line x1="0" y1="1" x2="40" y2="1" stroke="currentColor" strokeDasharray="3 4" data-id="element-5569" />
                </svg>
                {distanceText && <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mt-1" data-id="element-5570">
                    {distanceText}
                  </p>}
              </div>
              <div className="flex items-center gap-2.5 min-w-0 flex-row-reverse text-right" data-id="element-5571">
                <div className="w-9 h-9 rounded-xl bg-gray-900 flex items-center justify-center shrink-0" data-id="element-5572">
                  <MapPin className="w-4 h-4 text-white" fill="currentColor" data-id="element-5573" />
                </div>
                <div className="min-w-0" data-id="element-5574">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400" data-id="element-5575">
                    Dropoff
                  </p>
                  <p className="font-bold text-gray-900 text-sm truncate" data-id="element-5576">
                    {dynamicDropoff || service.summary.dropoff}
                  </p>
                </div>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}